import { maybeAppendCopyToText } from "@indietabletop/appkit/append-copy-to-text";
import { ModernIDB } from "../../oss/modern-idb/index.js";
import { getId } from "../nanoid.js";
import { idbValidKeyToString } from "../valid-key-to-string.js";
import { initDatabase } from "./init.js";
import { ArmyData, ArmyDataBase, Indexes, Schema } from "./types.js";

export class EternolDB {
  idb: ModernIDB<Schema, Indexes>;

  constructor() {
    this.idb = new ModernIDB({
      name: "eternol",
      version: 1,
      onInit: initDatabase,
    });
  }

  /**
   * Warning: This method does not emit change events on the DB instance!
   */
  async clearAll() {
    const [stores, done] = this.idb.transaction(["armies"], "readwrite", {
      noEmit: true,
    });

    await Promise.all([...stores.map((store) => store.clear()), done]);
  }

  async getArmies() {
    const [store, done] = this.idb.transaction("armies");
    const index = store.index("created_ts_idx");
    const [data] = await Promise.all([index.getAll(), done]);

    return data.filter((a): a is ArmyData => !a.deleted);
  }

  async getArmy(armyId: string) {
    const armyOrTombstone = await this.idb.getFromStore("armies", armyId);

    if (armyOrTombstone?.deleted) {
      return null;
    }

    return armyOrTombstone;
  }

  async deleteArmy(army: ArmyData) {
    const [store, done] = this.idb.transaction("armies", "readwrite");

    await Promise.all([
      store.put({
        id: army.id,
        name: army.name,
        deleted: true,
        updated_ts: Date.now(),
      }),
      done,
    ]);
  }

  async createArmy(
    data: Pick<ArmyData, "name" | "description" | "ruleset_version"> &
      Pick<Partial<ArmyData>, "units" | "keywords">,
  ) {
    const now = Date.now();

    const idKey = await this.idb.putToStore("armies", {
      id: getId(),
      name: data.name,
      description: data.description,
      share_id: null,
      created_ts: now,
      updated_ts: now,
      ruleset_version: data.ruleset_version,
      deleted: false,
      keywords: data.keywords ?? [],
      units: data.units ?? [],
    });

    return idbValidKeyToString(idKey);
  }

  async updateArmy(data: ArmyData, options?: { allowSetShareId?: boolean }) {
    // By default we always reset the share id to make sure that a share only
    // ever points to an army with that exact configuration.
    const { allowSetShareId = false } = options ?? {};

    return this.idb.putToStore("armies", {
      ...data,
      share_id: allowSetShareId ? data.share_id : null,
      updated_ts: Date.now(),
    });
  }

  async cloneArmy(
    data: ArmyDataBase,
    options?: { disambiguateName?: boolean },
  ) {
    const { disambiguateName = false } = options ?? {};
    const now = Date.now();

    const idKey = await this.idb.putToStore("armies", {
      id: getId(),
      name: disambiguateName ? maybeAppendCopyToText(data.name) : data.name,
      description: data.description,
      share_id: null,
      created_ts: now,
      updated_ts: now,
      ruleset_version: data.ruleset_version,
      deleted: false,
      keywords: data.keywords,
      units: data.units.map((u) => {
        return {
          ...u,
          id: getId(),
          created_ts: now,
          updated_ts: now,
        };
      }),
    });

    return idbValidKeyToString(idKey);
  }
}
